import * as Cl from '@design-system/component-library';
import { Link } from 'react-router';
import { dsClass } from '../../../common/constants/dsClasses.js';
import { formatPhoneNumber } from '../../../common/utils/phoneNumberUtils.js';
import { highlightKeyword } from '../../../common/utils/searchFieldUtils.js';
import { HighlightFormattedPhoneNumber } from '../../Highlight/HighlightFormattedPhoneNumber.js';
import type { CLTableRow } from '../../../common/types/componentLibrary';
import type { SubscriptionPbxType } from '../../../generated/api/subscriptionPbxType.js';

export interface CallerDisplayNameRowData {
  id: string;
  phoneNumber?: string;
  displayUser: string;
  displayCompany: string;
  activeDisplayName: string;
  originalDisplayName: string;
  subscriptionDisplayId: string;
  subscriptionLink: string;
  isOverride: boolean;
  pbxType?: SubscriptionPbxType;
  corporateNumber?: string;
  corporateActiveDisplayName?: string;
  corporateOriginalDisplayName?: string;
  corporateDisplayUser?: string;
  corporateDisplayCompany?: string;
}

export const WithCorporateSecondaryLine = ({
  main,
  secondary,
  pbxType,
}: {
  main: React.ReactNode;
  secondary?: React.ReactNode;
  pbxType?: SubscriptionPbxType;
}) => (
  <span>
    {main}
    {secondary !== undefined && (
      <span className={dsClass.DISPLAY_BLOCK}>
        <small>
          {pbxType === 'VAKIO' ? 'Vakio' : 'Ring'}: {secondary}
        </small>
      </span>
    )}
  </span>
);

export const CallerDisplayPhoneNumberCell = ({
  row,
  searchQuery,
}: {
  row: CallerDisplayNameRowData;
  searchQuery?: string;
}) => (
  <WithCorporateSecondaryLine
    main={
      row.phoneNumber ? (
        <HighlightFormattedPhoneNumber formattedPhoneNumber={formatPhoneNumber(row.phoneNumber)} search={searchQuery} />
      ) : null
    }
    secondary={
      row.corporateNumber ? (
        <HighlightFormattedPhoneNumber
          formattedPhoneNumber={formatPhoneNumber(row.corporateNumber, true)}
          search={searchQuery}
        />
      ) : undefined
    }
    pbxType={row.pbxType}
  />
);

export const formatCallerDisplayNameRowView = (
  row: CallerDisplayNameRowData,
  onEditRow: (row: CallerDisplayNameRowData) => void,
  onResetRow: (row: CallerDisplayNameRowData) => void,
  searchQuery?: string
) =>
  ({
    subscriptionLink: <Link to={row.subscriptionLink}>{row.subscriptionDisplayId}</Link>,
    phoneNumber: <CallerDisplayPhoneNumberCell row={row} searchQuery={searchQuery} />,
    activeDisplayName: (
      <WithCorporateSecondaryLine
        main={highlightKeyword(row.activeDisplayName, searchQuery)}
        secondary={
          row.corporateNumber && row.corporateActiveDisplayName !== undefined
            ? highlightKeyword(row.corporateActiveDisplayName, searchQuery)
            : undefined
        }
        pbxType={row.pbxType}
      />
    ),
    originalDisplayName: (
      <WithCorporateSecondaryLine
        main={row.originalDisplayName}
        secondary={row.corporateNumber ? row.corporateOriginalDisplayName : undefined}
        pbxType={row.pbxType}
      />
    ),
    displayUser: (
      <WithCorporateSecondaryLine
        main={row.displayUser}
        secondary={row.corporateNumber && row.corporateDisplayUser !== undefined ? row.corporateDisplayUser : undefined}
        pbxType={row.pbxType}
      />
    ),
    displayCompany: (
      <WithCorporateSecondaryLine
        main={row.displayCompany}
        secondary={
          row.corporateNumber && row.corporateDisplayCompany !== undefined ? row.corporateDisplayCompany : undefined
        }
        pbxType={row.pbxType}
      />
    ),
    actionButtons: (
      <div key={`${row.id}-view`} className={`${dsClass.DISPLAY_FLEX} ${dsClass.JUSTIFY_CONTENT_CENTER}`}>
        <Cl.Button
          color="link"
          className={`${dsClass.BORDER_STYLE_NONE} ${dsClass.PADDING_VERTICAL_0} ${dsClass.MARGIN_RIGHT_2}`}
          size="s"
          onClick={() => onEditRow(row)}
        >
          <Cl.Icon icon="edit" color="brand-blue" />
        </Cl.Button>
        <Cl.Button
          color="link"
          className={`${dsClass.BORDER_STYLE_NONE} ${dsClass.PADDING_VERTICAL_0}`}
          size="s"
          onClick={() => onResetRow(row)}
        >
          <Cl.Icon icon="redo" color="brand-blue" />
        </Cl.Button>
      </div>
    ),
  }) satisfies CLTableRow;
